import { Dispatch } from "@reduxjs/toolkit";
import AuthenticatedPage from "../../pages/auth/AuthenticatedPage";
import { getCloudBackend } from "../../store/selectors";
import BackendApiInterface from "../backend-api-interface";
import { CloudAuthBackendClient, getCloudAuthBackendClient } from "../cloud-backend-auth";
import store from "../../store/store";
import { MVisionAppClient } from "../../store/mvision-client-list";
import { convertResponseToError } from "../../util/errors";
import _ from "lodash";

const apiRoot = '/config';
const getLicenseUrl = apiRoot + '/get-licenses';
const getModelCustomizationUrl = apiRoot + '/get-segmentation-config';
const saveModelCustomizationUrl = apiRoot + '/save-segmentation-config';
const deleteModelCustomizationUrl = apiRoot + '/delete-segmentation-config';
const getListActiveModelsUrl = apiRoot + '/list-active-models';
const getClientListUrl = apiRoot + '/get-user-list';
const getLabelingUrl = apiRoot + '/labeling';
const getAllDaemonConfigsUrl = apiRoot + '/get-daemon-configs';
const getDaemonConfigUrl = apiRoot + '/get-daemon-config';
const saveDaemonConfigUrl = apiRoot + '/save-daemon-config';
const resetDaemonConfigUrl = apiRoot + '/reset-daemon-config';
const deleteDaemonConfigUrl = apiRoot + '/delete-daemon-config';

/**
 * Wrapper for accessing Config UI functions through MVision cloud.
 */
export default class CloudApi implements BackendApiInterface {

    /** Returns a backend client for any backend API calls matching whatever is the current
     * cloud backend.
     */
    getCloudAuthBackendClient(): CloudAuthBackendClient {
        const backend = getCloudBackend(store.getState());
        if (backend === undefined) {
            throw new Error(`Could not get current cloud backend.`);
        }

        return getCloudAuthBackendClient(backend);
    }

    async init(dispatch: Dispatch) {
        // no-op init
    }

    private getErrorMessageFromErrorJson(json: any): string {
        return _.has(json, 'detail') ? `An error was received from backend: ${json.detail}` : `An error was received from backend.`;
    }

    /** Performs a GET fetch request to backend. */
    private async fetchFromApi(url: string, headers?: any): Promise<any> {
        const client = this.getCloudAuthBackendClient();
        const response = await client.fetch(url, { headers: headers || {} });
        const json = await response.json();

        if (!response || response.status !== 200) {
            const message = this.getErrorMessageFromErrorJson(json);
            console.error(message);
            console.log(json);
            throw new Error(message);
        }

        return json;
    }

    /** Sends a POST request to backend, with an optional JSON body and optional headers. */
    private async postToApi(url: string, headers?: any, jsonData?: string): Promise<any> {
        const client = this.getCloudAuthBackendClient();
        const data = jsonData;

        const httpRequestOptions: any = {};
        if (jsonData) { httpRequestOptions['body'] = data; }
        if (headers) { httpRequestOptions['headers'] = headers; }
        const response = await client.post(url, httpRequestOptions, { postJson: true });
        const json = await response.json();

        if (!response || response.status !== 200) {
            const message = this.getErrorMessageFromErrorJson(json);
            console.error(message);
            console.log(response);
            if (jsonData) {
                throw await convertResponseToError(json, jsonData, message);
            } else {
                throw new Error(message);
            }
        }

        return json;
    }

    /** Performs a DELETE request to backend. */
    private async deleteFromApi(url: string, headers: any): Promise<boolean> {
        const client = this.getCloudAuthBackendClient();

        const response = await client.fetch(url, { headers: headers, 'method': 'delete' });
        const json = await response.json();

        if (!response || response.status !== 200) {
            const message = this.getErrorMessageFromErrorJson(json);
            console.error(message);
            console.log(json);
            throw new Error(message);
        }

        return json;
    }

    public async fetchModelConfigurationFileAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        if (appClient === undefined) {
            // return null if no app client was given
            return null;
        }

        return await this.fetchFromApi(getModelCustomizationUrl, { userId: appClient.userId });
    }

    public async saveModelConfigurationAsync(appClient: MVisionAppClient | undefined, json: string): Promise<boolean> {
        if (appClient === undefined) {
            // return null if no app client was given
            return false;
        }

        return await this.postToApi(saveModelCustomizationUrl, { userId: appClient.userId }, json);
    }

    public async deleteModelConfigurationAsync(appClient: MVisionAppClient | undefined): Promise<boolean> {
        if (appClient === undefined) {
            // return null if no app client was given
            return false;
        }

        return await this.deleteFromApi(deleteModelCustomizationUrl, { userId: appClient.userId });
    }

    public async fetchAllDaemonConfigsAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        if (appClient === undefined) {
            return null;
        }
        
        return await this.fetchFromApi(getAllDaemonConfigsUrl, { userId: appClient.userId });
    }

    public async fetchDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<any> {
        if (appClient === undefined) {
            return null;
        }
        
        return await this.fetchFromApi(getDaemonConfigUrl, { userId: appClient.userId, daemonSessionId: daemonSessionId });
    }

    public async saveDaemonConfigAsync(appClient: MVisionAppClient | undefined, json: string): Promise<boolean> {
        if (appClient === undefined) {
            return false;
        }

        return await this.postToApi(saveDaemonConfigUrl, { userId: appClient.userId }, json);
    }

    public async resetDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<boolean> {
        if (appClient === undefined || !daemonSessionId) {
            return false;
        }

        return await this.postToApi(resetDaemonConfigUrl, { userId: appClient.userId, daemonSessionId: daemonSessionId });
    }

    public async deleteDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<boolean> {
        if (appClient === undefined || !daemonSessionId) {
            return false;
        }

        return await this.deleteFromApi(deleteDaemonConfigUrl, { userId: appClient.userId, daemonSessionId: daemonSessionId });
    }

    public async fetchLicenseStatusAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        if (appClient === undefined) {
            // return an empty license if no app client was given
            return {};
        }

        return await this.fetchFromApi(getLicenseUrl, { userId: appClient.userId });
    }

    public async saveLicenseAsync(appClient: MVisionAppClient | undefined, newLicense: string): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public async fetchLabelingAsync(): Promise<any> {
        return await this.fetchFromApi(getLabelingUrl);
    }

    public async fetchActiveModelsAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        if (appClient === undefined) {
            // return null if no app client was given
            return null;
        }

        return await this.fetchFromApi(getListActiveModelsUrl, { userId: appClient.userId });
    }

    public async fetchClientListAsync(): Promise<any> {
        const client = this.getCloudAuthBackendClient();
        const response = await client.fetch(getClientListUrl);
        const json = await response.json();

        if (!response || response.status !== 200) {
            const message = this.getErrorMessageFromErrorJson(json);
            console.error(message);
            console.log(json);
            throw new Error(message);
        }

        return json;
    }

    public getAppWrapperComponent(): (props: React.PropsWithChildren<{}>) => JSX.Element {
        return AuthenticatedPage;
    }
}
